import {Alert, Box, Stack, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {FontPickerFields} from "../../common/newFontPicker";
import {signingThemeToMuiTheme} from "../public/signingTheme";
import {DigitalSigningDesign} from "./digitalSigningTemplate.model";

/** The design's font: its source, link and family change together. */
type FontValue = Pick<DigitalSigningDesign, 'fontSource' | 'fontUrl' | 'fontFamily'>;

interface Props {
    value: FontValue
    onChange: (value: FontValue) => void
    errors?: {fontUrl?: string}
    disabled?: boolean
}

/** The page's font: the shared font picker, a note on Standard, and a preview like the page. */
const DigitalSigningFontFields = ({value, onChange, errors, disabled}: Props) => {
    const {t} = useTranslation();
    const {fontSource, fontFamily} = value;

    const {h1: title, body1: text} = useMemo(
        () => signingThemeToMuiTheme({fontFamily: fontFamily ?? undefined}).typography,
        [fontFamily]
    );

    return (
        <Stack gap={{xs: 1.5, sm: 1}}>
            <FontPickerFields value={value} onChange={onChange} errors={errors} disabled={disabled}/>

            {/* Names what SYSTEM_SANS and SERIF_STACK (signingFonts) resolve to - keep it in step with them */}
            {fontSource === 'STANDARD' && (
                <Property title={''}>
                    <Alert severity={'info'} variant={'outlined'} sx={{flex: 1, minWidth: 0, alignItems: 'center', py: 0}}>
                        {t('Standard uses the browser\'s fallback fonts, so the page looks a little different on each device: on iOS San Francisco with Iowan Old Style titles, on Android Roboto with Noto Serif titles.')}
                    </Alert>
                </Property>
            )}

            <Property title={t('Preview')} labelAlignment={'top'}>
                <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, width: '100%'}}>
                    <Typography component={'div'} sx={{...title, mb: 1}}>
                        {t('A moment for you')}
                    </Typography>
                    <Typography component={'div'} sx={text}>
                        {t('The quick brown fox jumps over the lazy dog. 0123456789 !?&%$')}
                    </Typography>
                </Box>
            </Property>
        </Stack>
    );
};

export default DigitalSigningFontFields;
